import { useState, useMemo } from 'react';
import {
  Loader2,
  Moon,
  Play,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useNightAuditHistory, useNightAuditDetail, useRunNightAudit } from '@/hooks/api/useNightAudit';
import type { NightAuditLog, NightAuditSummary } from '@/services/api/night-audit.api';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<string, { icon: typeof CheckCircle2; bg: string; text: string; label: string }> = {
  completed: { icon: CheckCircle2, bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  in_progress: { icon: Clock, bg: 'bg-blue-100', text: 'text-blue-700', label: 'In Progress' },
  pending: { icon: Clock, bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
  failed: { icon: XCircle, bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
};

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return '--';
  try {
    return format(parseISO(iso), 'MMM d, yyyy h:mm a');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function NightAuditPage() {
  const propertyId = useSelectedPropertyId();
  const [selectedAuditId, setSelectedAuditId] = useState<string | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [page, setPage] = useState(1);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const PAGE_SIZE = 20;

  const { data: historyData, isLoading } = useNightAuditHistory({ propertyId: propertyId || undefined, page, limit: PAGE_SIZE });
  const runAudit = useRunNightAudit();

  const allAuditLogs: NightAuditLog[] = historyData?.data ?? [];
  const totalPages = historyData?.meta?.totalPages ?? 1;

  // Client-side date filter on businessDate
  const auditLogs: NightAuditLog[] = useMemo(() => {
    let filtered = allAuditLogs;
    if (fromDate) {
      filtered = filtered.filter((log) => log.businessDate >= fromDate);
    }
    if (toDate) {
      filtered = filtered.filter((log) => log.businessDate <= toDate);
    }
    return filtered;
  }, [allAuditLogs, fromDate, toDate]);

  const handleRunAudit = () => {
    setShowConfirm(false);
    if (!propertyId) return; // a property must be selected first
    runAudit.mutate({ propertyId });
  };

  return (
    <div>
      <PageHeader
        title="Night Audit"
        description="Run daily night audits and review history"
        actions={
          <Button onClick={() => setShowConfirm(true)} isLoading={runAudit.isPending}>
            <Play size={16} aria-hidden="true" />
            Run Night Audit
          </Button>
        }
      />

      {/* Confirmation dialog */}
      {showConfirm && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-yellow-600 shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">Confirm Night Audit</p>
            <p className="text-sm text-yellow-700 mt-1">This compiles end-of-day occupancy, revenue, and reconciliation statistics for the selected property and records a night audit log entry.</p>
            <div className="flex items-center gap-3 mt-3">
              <Button size="sm" onClick={handleRunAudit} isLoading={runAudit.isPending}>
                Confirm & Run
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setShowConfirm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Success/error message */}
      {runAudit.isSuccess && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 size={20} className="text-green-600 shrink-0" aria-hidden="true" />
          <p className="text-sm text-green-700">Night audit completed successfully.</p>
        </div>
      )}
      {runAudit.isError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <XCircle size={20} className="text-red-600 shrink-0" aria-hidden="true" />
          <p className="text-sm text-red-700">Night audit failed. Please check the audit log for details.</p>
        </div>
      )}

      {/* Date filter */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label htmlFor="na-from" className="text-sm font-medium text-gray-700">From</label>
          <input
            id="na-from"
            type="date"
            value={fromDate}
            onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="na-to" className="text-sm font-medium text-gray-700">To</label>
          <input
            id="na-to"
            type="date"
            value={toDate}
            onChange={(e) => { setToDate(e.target.value); setPage(1); }}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        {(fromDate || toDate) && (
          <button
            type="button"
            onClick={() => { setFromDate(''); setToDate(''); setPage(1); }}
            className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Audit history table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : auditLogs.length === 0 ? (
        <EmptyState icon={Moon} title="No audit history" description="Run your first night audit to see history here." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Business Date</Th>
                  <Th>Status</Th>
                  <Th>Started</Th>
                  <Th>Completed</Th>
                  <Th>Performed By</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {auditLogs.map((log) => {
                  const cfg = STATUS_CONFIG[log.status] ?? STATUS_CONFIG.pending;
                  return (
                    <tr
                      key={log.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedAuditId(log.id)}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{fmtDate(log.businessDate)}</td>
                      <td className="px-4 py-3">
                        <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium', cfg.bg, cfg.text)}>
                          {cfg.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDateTime(log.startedAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDateTime(log.completedAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500 font-mono">{log.performedBy.slice(0, 8)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Previous
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-50 hover:bg-gray-50 transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Detail slide-over */}
      <SlideOver
        isOpen={selectedAuditId !== null}
        onClose={() => setSelectedAuditId(null)}
        title="Audit Detail"
        width="max-w-xl"
      >
        {selectedAuditId !== null && <AuditDetail id={selectedAuditId} />}
      </SlideOver>
    </div>
  );
}

// ─── Audit Detail ────────────────────────────────────────────────────────────

function AuditDetail({ id }: { id: string }) {
  const { data: detail, isLoading } = useNightAuditDetail(id);

  if (isLoading || !detail) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  const s: NightAuditSummary = detail.summary;
  const cfg = STATUS_CONFIG[detail.status] ?? STATUS_CONFIG.pending;

  // Resolve values: prefer backend field names, fall back to legacy names
  const revenue = s.revenue !== undefined ? Number(s.revenue) : (s.totalRoomRevenue !== undefined ? Number(s.totalRoomRevenue) : undefined);
  const occupied = s.occupied ?? s.inHouseCount;
  const totalRooms = s.totalRooms;
  const occupancy = s.occupancyRate ?? s.occupancyPercentage;
  const arrivals = s.arrivals ?? s.arrivalsCount;
  const departures = s.departures ?? s.departuresCount;
  const adr = revenue !== undefined && occupied ? (revenue / occupied) : (s.adr !== undefined ? Number(s.adr) : undefined);
  const revPar = revenue !== undefined && totalRooms ? (revenue / totalRooms) : (s.revPar !== undefined ? Number(s.revPar) : undefined);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <span className={cn('inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium', cfg.bg, cfg.text)}>
          {cfg.label}
        </span>
        <span className="text-sm text-gray-500">Business Date: {fmtDate(detail.businessDate)}</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <SummaryStat label="Revenue" value={revenue !== undefined ? `$${Number(revenue).toFixed(2)}` : '--'} />
        <SummaryStat label="Total Tax" value={s.totalTax ? `$${s.totalTax}` : 'N/A'} />
        <SummaryStat label="Total Payments" value={s.totalPayments ? `$${s.totalPayments}` : 'N/A'} />
        <SummaryStat label="Occupancy" value={occupancy !== undefined ? `${occupancy}%` : '--'} />
        <SummaryStat label="ADR" value={adr !== undefined ? `$${adr.toFixed(2)}` : 'N/A'} />
        <SummaryStat label="RevPAR" value={revPar !== undefined ? `$${revPar.toFixed(2)}` : 'N/A'} />
        <SummaryStat label="Arrivals" value={arrivals !== undefined ? String(arrivals) : '--'} />
        <SummaryStat label="Departures" value={departures !== undefined ? String(departures) : '--'} />
        <SummaryStat label="No Shows" value={s.noShowsCount !== undefined ? String(s.noShowsCount) : '0'} />
        <SummaryStat label="In House" value={occupied !== undefined ? String(occupied) : '--'} />
        <SummaryStat label="Total Rooms" value={totalRooms !== undefined ? String(totalRooms) : '--'} />
        {s.unreconciledCount !== undefined && (
          <SummaryStat label="Unreconciled" value={`${s.unreconciledCount} ($${Number(s.unreconciledTotal ?? 0).toFixed(2)})`} />
        )}
      </div>

      {s.exceptions && s.exceptions.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Exceptions</h4>
          <ul className="space-y-1">
            {s.exceptions.map((ex, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-red-600">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" aria-hidden="true" />
                {ex}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-xs text-gray-400 pt-4 border-t border-gray-100">
        <p>Started: {fmtDateTime(detail.startedAt)}</p>
        <p>Completed: {fmtDateTime(detail.completedAt)}</p>
      </div>
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg p-3" aria-label={`${label}: ${value}`}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-lg font-bold text-gray-900">{value}</p>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
